import React, { useState } from "react";
import { JhText } from "designPackages/textPackage";
import { JhButton } from "designPackages/buttonPackage";
import { Users, RefreshCcw, UsersRound } from "lucide-react";
import { applyTagStyles, applyMessageStyles } from "./jobMetaConfig";
import LogService from "services/LogService";
import { useRouter } from "next/router";
import {
  CANDIDATE_LISTING,
  JOB_POSTING_FORM,
  Routes
} from "routes/RouteConstants";
import JobListEvents from "utils/Events/jobListEvents";
import useCreditProfileStore from "zustandStores/userCreditProfileStore";
import JobCardMenu from "./JobCardMenu";
import dynamic from "next/dynamic";

const CloseJobDrawer = dynamic(
  () => import("components/Dashboard/CloseJobsDrawer")
);
import { useSelector } from "react-redux";
import { useIsMobile } from "hooks/useDeviceType";
import TAB_VALUES from "./tabValues";
import JobCardMiddleSection from "./JobCardMiddleSection";
import CustomTag from "./CustomTag";
import StatBox from "./StatBox";
import MessageBanner from "./MessageBanner";
import { useTabStore } from "zustandStores/tabStore";
import { JhUseToast } from "designPackages/toastPackage";
import JobService from "services/JobService";
import { RESPONSE_MANAGER_V3_TABS } from "constants/responseManagerV3Constants";
// import { useTabsWithStore } from "queries/tabs/useTabsWithStore";
// Define a type for the Redux state to fix the dashboardReducer error
interface RootState {
  dashboardReducer: {
    closeJobMetadata: {
      payload: any;
    };
  };
}

interface JobCardV2Props {
  job: any; // Using any to accommodate our job schema
  activeTab: string;
  isLoading?: boolean;
}

// Job Card Component
const JobCardV2: React.FC<JobCardV2Props> = ({ job, activeTab, isLoading }) => {
  const router = useRouter();
  const { toast } = JhUseToast();
  // Apply meta styling to job data
  const styledTags = applyTagStyles(job.tags);
  const styledMessaging = applyMessageStyles(job.messaging);
  const closeJobMetadata = useSelector(
    (state: RootState) => state.dashboardReducer.closeJobMetadata.payload
  );
  const [closeJobDrawer, setCloseJobDrawer] = useState(false);
  const isMobile = useIsMobile();
  const { fetchCreditProfile } = useCreditProfileStore(state => ({
    fetchCreditProfile: state.fetchData
  }));
  // Get removeJob and tabData from tabStore
  const { removeJob, updateSingleJob, updateSingleJobLocal, fetchTabs } =
    useTabStore();

  const onJobUpgrade = async (response, data) => {
    if (isLoading) return;
    try {
      if (response.isDraft) {
        router.push(`${Routes.CHECKOUT}?flow=upgrade&jobId=${job.job_id}`);
      } else {
        fetchCreditProfile();
        await updateSingleJob(job.job_id);
        toast({
          title: "Your job has been upgraded to a Premium Job",
          variant: "default"
        });
      }
    } catch (err) {
      console.log("Err");
    }
  };
  const onGoToCmpClick = type => {
    if (isLoading) return;
    const { job_title, job_id, show_under_review_reco } = job;
    let eventType =
      type === "pending"
        ? JobListEvents.JOB_LIST_UNACTIONED_CLICKED
        : JobListEvents.VIEW_ALL_CANDIDATES;
    if (show_under_review_reco)
      eventType = JobListEvents.UNDER_REVIEW_VIEW_ALL_CANDIDATE_CLICKED;
    if (type === "database") {
      eventType = JobListEvents.DATABASE_CLICKED;
    }
    if (type === "hired") {
      eventType = JobListEvents.VIEW_HIRED_CANDIDATES;
    }
    LogService.log(eventType, {
      job_id: job_id,
      job_title: job_title
    });
    if (type === "database") {
      router.push({
        pathname: Routes.DATABASE_PRODUCT,
        query: { job_id }
      });
      return;
    }
    if (type === "hired") {
      router.push({
        pathname: CANDIDATE_LISTING,
        query: { job_id, tab_id: RESPONSE_MANAGER_V3_TABS.HIRED }
      });
      return;
    }
    router.push({
      pathname: CANDIDATE_LISTING,
      query: type === "actioned" ? { job_id, tab: type } : { job_id }
    });
  };
  const openJp = (event, type) => {
    if (isLoading) return;
    // Prevent event propagation to avoid issues
    event.preventDefault();
    event.stopPropagation();
    let query: { job_id: any; re_post?: boolean; is_draft?: boolean } = {
      job_id: job.job_id
    };
    if (type === "draft") {
      query.is_draft = true;
    }
    let eventName;
    if (type === "edit") eventName = JobListEvents.EDIT_JOB_CLICKED;
    else if (type === "draft")
      eventName = JobListEvents.DASHBOARD_COMPLETE_DRAFT_CLICKED;
    else if (type === "re_post") {
      query.re_post = true;
      eventName = JobListEvents.DASHBOARD_REPOST_JOB_CLICKED;
    }

    LogService.log(eventName, {
      job_id: job.job_id
    });
    router
      .push({
        pathname: JOB_POSTING_FORM,
        query
      })
      .then(() => {
        window.scrollTo(0, 0);
      });
  };

  // Helper function to determine if the bottom section should be rendered
  const shouldRenderBottomSection = () => {
    // Live tab with stats to show
    if (activeTab === TAB_VALUES.live) {
      return true;
    }

    // All Candidates button available
    if (
      job.show_view_all_candidates === "enabled" ||
      job.show_view_all_candidates === "disabled"
    ) {
      return true;
    }

    // Job is repostable
    if (job?.is_job_repostable) {
      return true;
    }

    // Closed job showing hired candidates
    if (job.show_hired_candidates && activeTab === TAB_VALUES.closed) {
      return true;
    }

    // Draft job
    if (activeTab === TAB_VALUES.draft) {
      return true;
    }

    return false;
  };

  return (
    <div
      id={job.job_id}
      className={`p-3 bg-white rounded-lg border border-solid border-border flex flex-col ${
        isLoading ? "opacity-75 pointer-events-none" : ""
      }`}
    >
      {/* Top Section */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2 flex-wrap">
          {/* Status tag is now included in the styledTags */}
          {styledTags.map((tag, index) => (
            <CustomTag key={index} tag={tag} />
          ))}
        </div>
        <JobCardMenu
          job={job}
          setCloseJobDrawer={setCloseJobDrawer}
          handleJobEdit={openJp}
        />
      </div>

      {/* Middle Section - Now using the extracted component */}
      <JobCardMiddleSection job={job} />

      {/* Messaging Banner - from backend */}
      {styledMessaging && (
        <MessageBanner
          styledMessaging={styledMessaging}
          onJobUpgrade={onJobUpgrade}
          job={job}
          onHiringNudgeResponse={async response => {
            if (isLoading) return;
            if (response) {
              // User clicked Yes - open close job drawer
              setCloseJobDrawer(true);
            } else {
              // User clicked No - hide the message by updating the job in state
              LogService.log(JobListEvents.HIRING_NUDGE_RESPONSE, {
                job_id: job.job_id,
                response: "dismissed"
              });
              await JobService.stillHiring(job.job_id);
              const updatedJob = { ...job, show_hiring_nudge: false };
              updateSingleJobLocal(job.job_id, updatedJob);
            }
          }}
        />
      )}

      {/* Bottom Section - Only render if there are elements to display */}
      {shouldRenderBottomSection() && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-0 justify-between pt-2.5 border-t border-border ">
          {/* Stats */}
          {(activeTab === TAB_VALUES.live ||
            (activeTab === TAB_VALUES.review &&
              job.show_under_review_reco)) && (
            <div
              className={`flex w-full gap-2 ${
                !job.database_count || job.database_count === 0
                  ? "justify-start"
                  : "justify-between"
              } sm:justify-start`}
            >
              <StatBox
                onClick={
                  job.show_view_all_candidates !== "enabled" ||
                  isLoading ||
                  !job.allow_action
                    ? undefined
                    : () => onGoToCmpClick("pending")
                }
                count={job.pending_count}
                newCount={job.new_count}
                label="To Review"
                bgColor="var(--info)"
                iconColor="var(--info-foreground)"
                className={`flex-1 sm:w-auto sm:flex-initial ${
                  job.show_view_all_candidates !== "enabled" ||
                  isLoading ||
                  !job.allow_action
                    ? "cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              />
              {job.database_count && activeTab === TAB_VALUES.live ? (
                <StatBox
                  count={job.database_count}
                  label="Database"
                  bgColor="var(--accent)"
                  iconColor="var(--primary)"
                  className="flex-1 sm:w-auto sm:flex-initial cursor-pointer"
                  onClick={
                    isLoading || !job.allow_action
                      ? undefined
                      : () => onGoToCmpClick("database")
                  }
                />
              ) : null}
            </div>
          )}

          {/* Bottom Buttons */}
          {/* for live jobs */}
          <div className="w-full flex gap-2 sm:justify-end">
            {(job.show_view_all_candidates === "enabled" ||
              job.show_view_all_candidates === "disabled") && (
              <JhButton
                size="xs"
                onClick={isLoading ? undefined : () => onGoToCmpClick("")}
                disabled={
                  job.show_view_all_candidates === "disabled" || isLoading
                }
                className="flex items-center gap-1 flex-1 sm:flex-initial"
              >
                <UsersRound size={16} color="white" />
                All Candidates
                {Number(job?.total_candidates_count) > 0
                  ? ` · ${job.total_candidates_count}`
                  : ""}
              </JhButton>
            )}

            {/* For closed jobs, show "View Hired" if applicable */}

            {job?.is_job_repostable && (
              <JhButton
                size="xs"
                variant="outline"
                className="flex items-center gap-1 flex-1 sm:flex-initial"
                onClick={isLoading ? undefined : e => openJp(e, "re_post")}
                disabled={isLoading}
              >
                <RefreshCcw size={16} />
                Repost Job
              </JhButton>
            )}
            {job.show_hired_candidates && activeTab === TAB_VALUES.closed && (
              <JhButton
                onClick={isLoading ? undefined : () => onGoToCmpClick("hired")}
                size="sm"
                variant="outline"
                className="flex items-center gap-1 flex-1 sm:flex-initial"
                disabled={isLoading}
              >
                <Users size={16} color="var(--primary)" />
                <JhText variant="caption1" color="primary">
                  Hired Candidates
                </JhText>
              </JhButton>
            )}
            {/* For draft jobs, show "Complete Job" */}
            {activeTab === TAB_VALUES.draft && job.status === "DRAFT" && (
              <JhButton
                size="xs"
                variant="outline"
                className="flex items-center gap-1 flex-1 sm:flex-initial"
                onClick={isLoading ? undefined : e => openJp(e, "draft")}
                disabled={isLoading}
              >
                Complete Job
              </JhButton>
            )}
          </div>
        </div>
      )}

      <CloseJobDrawer
        jobId={job.job_id}
        closeJobMetadata={closeJobMetadata}
        isClosed={closeJobDrawer}
        toggleDrawer={() => setCloseJobDrawer(!closeJobDrawer)}
        activeTab={activeTab}
        isMobile={isMobile}
        handleJobClose={data => {
          try {
            // Log the job close action
            LogService.log(JobListEvents.CLOSE_JOB_SUCCESS, {
              job_id: job.job_id,
              job_title: job.job_title,
              close_data: data
            });

            // Remove job from the current tab
            removeJob(job.job_id);
            fetchTabs();
            // refetchTabs();
          } catch (error) {
            console.error("Error closing job:", error);
          }
        }}
      />
    </div>
  );
};

export default JobCardV2;
